/**
 * The acquisition funnel — the six steps between "an organizer pressed share"
 * and "a stranger has an account".
 *
 *   share_pressed        a link left the app             (organizer)
 *   preview_viewed       someone opened it               (the door)
 *   guest_join_started   they tapped Unirme
 *   guest_join_failed    …and did not get in (+ reason)
 *   guest_join_completed …or did
 *   guest_signup_started they tapped Crear mi perfil
 *   ↳ `onboarding_completed` already exists and now carries `from_guest`.
 *
 * WHY this is a module and not six `track()` literals scattered across three
 * files: the funnel is only readable if every step names the same match with
 * the same param, and string literals drift the moment someone adds a screen.
 * `doorState` in particular has to keep mirroring the render gate in
 * match-preview-page — pure and tested here, so it can be checked against that
 * gate instead of guessed at.
 *
 * READING these events: GA4's standard reports suppress rows below a data
 * threshold and lag ~24 h, so at the tens-of-users scale this funnel exists to
 * measure they will show blanks. Use DebugView to confirm an event fires, and
 * the BigQuery export for the actual counts — event-level rows, no threshold.
 * The export is a one-time link in the console; it is not wired by this code.
 */
import { AUTH_BYPASS } from './auth/dev-bypass'
import { track } from './analytics'

/** How the link left the app. Only channels that hand someone a URL. */
export type ShareChannel = 'sheet' | 'clipboard' | 'whatsapp' | 'contacts'

/**
 * What the door offered the person who opened the link. One categorical
 * instead of four booleans, because the question this answers — "where do
 * people die?" — is a breakdown, not a filter.
 *
 * `error` is never returned by `doorState`: a failed READ and a match that
 * genuinely no longer exists render the same "ya no está disponible" copy, so
 * only the call site can tell them apart. Keeping them separate here is the
 * difference between "matches are expiring before people tap" and "our reads
 * are being denied" — two conclusions with nothing in common.
 */
export type DoorState = 'joinable' | 'full' | 'restricted' | 'closed' | 'gone' | 'error'

/** A full account opening a shared link is not an acquisition; keep them apart. */
export type DoorAudience = 'guest' | 'member'

/**
 * Mirrors the render gate in match-preview-page: the join block appears only
 * for an active match with a free seat, and inside it a gendered match asks
 * for an account instead. Order matters — a cancelled full match reads better
 * as 'closed' than as 'full'.
 */
export function doorState(
  // `genderCategory` is optional because it is optional on the match doc, and
  // the render gate reads an absent one as open (`=== 'men' || === 'women'`).
  // Requiring it here would have made this function disagree with the screen.
  match: { status: string; genderCategory?: string } | null | undefined,
  freeSlots: number,
): Exclude<DoorState, 'error'> {
  if (!match) return 'gone'
  // TERMINAL statuses only. This was `status !== 'active'`, which swallowed the
  // whole `full` bucket: a match that fills up is written `status: 'full'`
  // (match-membership.ts) and mapped to view status 'full' (use-matches.ts), so
  // every filled match logged as 'closed' — pooled with cancelled and played.
  // "shares work, supply is short" and "the match was over" are opposite
  // conclusions, and this collapsed them, on the release's headline metric.
  if (match.status === 'cancelled' || match.status === 'past') return 'closed'
  if (freeSlots <= 0) return 'full'
  // 'mixed' belongs here too, added 2026-08-11 with the 2+2 rule. Leaving it
  // out would have shipped exactly the defect this file already documents
  // elsewhere: a live-looking join button whose write the rules refuse, with
  // nothing on screen explaining why. A gendered match a stranger cannot join
  // must SAY it is gendered before they tap.
  if (
    match.genderCategory === 'men' ||
    match.genderCategory === 'women' ||
    match.genderCategory === 'mixed'
  )
    return 'restricted'
  return 'joinable'
}

/**
 * The design-stub walk (`npm run dev:preview`) drives every one of these
 * screens against fixture matches. Guarding once here — rather than at six
 * call sites — keeps a stub join from reading as a real conversion on the dev
 * property, and keeps the branch out of the screens themselves.
 */
function emit(name: string, params: Record<string, string | number | boolean | undefined>): void {
  if (AUTH_BYPASS) return
  track(name, params)
}

/**
 * A shareable link left the app. Deliberately NOT fired for in-app invites:
 * those produce no `preview_viewed`, so counting them here would break the
 * funnel's arithmetic — shares would exceed opens for a reason that has
 * nothing to do with people ignoring the link.
 *
 * This counts INTENT, not delivery. The Web Share API cannot tell us whether
 * the player picked a chat or dismissed the sheet (both surface as a resolved
 * promise or an AbortError we deliberately treat as success), so the
 * opens-per-share ratio reads low by however many sheets got dismissed.
 */
export function trackShare(
  matchId: string,
  channel: ShareChannel,
  freeSlots?: number,
  outcome?: string,
): void {
  emit('share_pressed', { match_id: matchId, channel, free_slots: freeSlots, outcome })
}

export function trackPreviewViewed(
  matchId: string,
  state: DoorState,
  audience: DoorAudience,
  privacy?: string,
): void {
  emit('preview_viewed', { match_id: matchId, door_state: state, audience, privacy })
}

export function trackGuestJoinStarted(matchId: string): void {
  emit('guest_join_started', { match_id: matchId })
}

/** `reason` is MembershipError's, plus `no_name` for the client-side bounce. */
export function trackGuestJoinFailed(matchId: string, reason: string): void {
  emit('guest_join_failed', { match_id: matchId, reason })
}

export function trackGuestJoinCompleted(matchId: string): void {
  emit('guest_join_completed', { match_id: matchId })
}

/** The seat is already theirs at this point — this is the tap for an account. */
export function trackGuestSignupStarted(matchId: string): void {
  emit('guest_signup_started', { match_id: matchId })
}

// ── THE BET (spec/02 § The bet, and how it gets measured) ────────────────────
// The whole placeholder model rests on one number: of the seats where somebody
// typed a name, what fraction does that person actually claim?
//
//   claim rate = placeholder_claimed / placeholder_created
//
// If nobody claims, a placeholder is a companion with a name and the machinery
// — a uid per seat, three rules paths, a CF attribution — bought a cosmetic
// improvement. If they do claim, every one is an identified player and it is
// the only organic growth loop the product has.
//
// These ship WITH the feature, never after. GA4 does not backfill, so a UI
// deployed uninstrumented makes the first month of the decision unmeasurable —
// exactly what happened to `login`/`sign_up`, documented for months and firing
// from nowhere.

export function trackPlaceholderCreated(matchId: string, freeSlotsAfter: number): void {
  emit('placeholder_created', { match_id: matchId, free_slots_after: freeSlotsAfter })
}

/**
 * `hours_since_created` is the half that makes the ratio honest. A claim after
 * the match has been played is not a conversion — the seat already did its job
 * — so the decision reads claims made BEFORE kickoff, and a raw
 * claimed/created ratio would flatter itself with late ones.
 */
export function trackPlaceholderClaimed(
  matchId: string,
  opts: { hoursSinceCreated?: number; beforeKickoff: boolean },
): void {
  emit('placeholder_claimed', {
    match_id: matchId,
    hours_since_created: opts.hoursSinceCreated,
    before_kickoff: opts.beforeKickoff,
  })
}
